import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bot, MessageCircle, Settings, X, Send } from 'lucide-react';
import { getFileUrl, getApiUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';
import ChatInterface from './ChatInterface';

interface ActiveShiftData {
  jobId: string;
  visitId: string;
  correspondentName: string;
  correspondentPhoto?: string;
  userType: 'customer' | 'caregiver'; 
  hasActiveSafetyCheck: boolean;
  onPinClick?: (pin: string) => void;
}

interface ShohAIProps {
  activeShift?: ActiveShiftData;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const ShohAI: React.FC<ShohAIProps> = ({ activeShift }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'ai' | 'human'>('human'); // Default to human if active shift
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [hasNewMessage, setHasNewMessage] = useState(false);
  
  // Poll for new messages if active shift exists but human chat is not active/open
  useEffect(() => {
     if (!activeShift) return;
     if (isOpen && activeTab === 'human') {
         setHasNewMessage(false); 
         return;
     }

     const checkNewMessages = async () => {
         try {
             const token = localStorage.getItem('token');
             // Simple optimization: fetch latest message timestamp or just get the list and check last
             const res = await axios.get(getApiUrl(`/api/chat/${activeShift.jobId}/${activeShift.visitId}`), {
                 headers: { Authorization: `Bearer ${token}` }
             });
             
             if (res.data.success && res.data.data.length > 0) {
                 const lastMsg = res.data.data[res.data.data.length - 1];
                 // If last message is NOT from me, and it's newer than... 
                 // For now, just simplistic: if not me, show dot. 
                 // A real unread system needs "lastReadAt" stored in DB or localstorage.
                 // We'll use a local session approximate for now or just "is not me"
                 // To make it sticky, we'd need to store 'lastViewedTime'.
                 
                 const myUserType = user?.userType === 'customer' ? 'Customer' : 'Caregiver';
                 if (lastMsg.senderType !== myUserType) {
                     // Check against last viewed time stored in state/ref?
                     // For this MVP, if the tab is closed, we assume unread if the latest is from other.
                     // But this will show dot forever until opened. That's actually expected behavior!
                     setHasNewMessage(true);
                 }
             }
         } catch(e) { console.error(e); }
     };

     const int = setInterval(() => {
       if (!document.hidden) checkNewMessages();
     }, 10000);
     checkNewMessages();
     return () => clearInterval(int);
  }, [activeShift, isOpen, activeTab, user]);

  const [isLoading, setIsLoading] = useState(false);
  const [dataAccessEnabled, setDataAccessEnabled] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [userProfilePic, setUserProfilePic] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch AI settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(getApiUrl('/api/ai/settings'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDataAccessEnabled(response.data.aiDataAccessEnabled);
      } catch (error) {
        console.error('Error fetching AI settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Fetch user profile picture
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return; // Don't fetch if no user logged in
      
      try {
        const token = localStorage.getItem('token');
        const endpoint = user.userType === 'customer' ? '/api/customers/me' : '/api/caregivers/me';
        const response = await axios.get(getApiUrl(endpoint), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.profilePhoto) {
          setUserProfilePic(getFileUrl(response.data.profilePhoto));
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();
  }, [user]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: "Hi! I'm shohAI, your PROYAS assistant. I can help you navigate the platform, answer questions, and provide insights. How can I help you today?",
          timestamp: new Date()
        }
      ]);
    }
  }, [isOpen, messages.length]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        getApiUrl('/api/ai/chat'),
        {
          message: inputMessage,
          enableDataAccess: dataAccessEnabled,
          conversationHistory: messages.map(m => ({
            role: m.role,
            content: m.content
          }))
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const aiMessage: Message = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error: any) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again later.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleDataAccess = async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        getApiUrl('/api/ai/settings'),
        { aiDataAccessEnabled: !dataAccessEnabled },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setDataAccessEnabled(response.data.aiDataAccessEnabled);
    } catch (error: any) {
      console.error('Error updating AI settings:', error);
      const errorMsg = error.response?.data?.message || 'Failed to update settings. Please try again.';
      setSettingsError(errorMsg);
      // Show error in chat
      const errorMessage: Message = {
        role: 'assistant',
        content: `⚠️ ${errorMsg}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderShohAIContent = () => (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-red-600 p-4 rounded-t-2xl shadow-lg flex justify-between items-center text-white shrink-0 z-10 sticky top-0">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-lg">shohAI</h3>
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            title="Settings"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-gray-50 p-4 border-b border-gray-200">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="dataAccess"
              checked={dataAccessEnabled}
              onChange={handleToggleDataAccess}
              disabled={settingsLoading}
              className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <div className="flex-1">
              <label htmlFor="dataAccess" className="text-sm font-semibold text-gray-900 cursor-pointer">
                Enable AI Data Access {settingsLoading && <span className="text-primary-600">...</span>}
              </label>
              <p className="text-xs text-gray-600 mt-1">
                Allow shohAI to access your account data for personalized insights and recommendations
              </p>
              {settingsError && (
                <p className="text-xs text-red-600 mt-2 font-semibold">
                  {settingsError}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="space-y-3">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex animate-slide-up ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              {message.role === 'assistant' && (
                <div className="flex items-end mr-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-red-500 flex items-center justify-center text-white shadow-lg">
                    <Bot size={18} />
                  </div>
                </div>
              )}
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-3 shadow-md transition-all hover:shadow-lg ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-primary-600 to-red-600 text-white rounded-br-md'
                    : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
                }`}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                <p
                  className={`text-xs mt-1.5 ${
                    message.role === 'user' ? 'text-primary-100' : 'text-gray-400'
                  }`}
                >
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              {message.role === 'user' && (
                <div className="flex items-end ml-2">
                  {userProfilePic ? (
                    <img
                      src={userProfilePic}
                      alt="Profile"
                      className="w-8 h-8 rounded-full object-cover shadow-lg border-2 border-gray-300"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-xs font-bold shadow-lg">
                      ME
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start animate-fade-in">
              <div className="bg-white rounded-2xl px-4 py-3 border border-gray-200 shadow-md">
                <div className="flex items-center gap-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-xs text-gray-500 font-medium">shohAI is thinking...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="p-3 bg-white border-t border-gray-200 rounded-b-xl flex gap-2">
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
          className="flex-1 border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          disabled={isLoading}
        />
        <button
          onClick={handleSendMessage}
          disabled={isLoading || !inputMessage.trim()}
          className="bg-primary-600 text-white p-2 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  const renderActiveShiftChat = () => (
    <div className="flex flex-col h-full bg-white relative">
        {activeShift && (
            <ChatInterface 
                jobId={activeShift.jobId} 
                visitId={activeShift.visitId} 
                currentUserType={activeShift.userType}
                hasActiveSafetyCheck={activeShift.hasActiveSafetyCheck}
                className="flex-1 border-0 shadow-none rounded-none h-full"
                onPinClick={activeShift.onPinClick}
                title={activeShift.correspondentName}
                onClose={() => setIsOpen(false)}
            />
        )}
    </div>
  );

  // If no active shift, default to simple single-button behavior
  if (!activeShift) {
      return (
        <div className="fixed bottom-24 sm:bottom-6 right-6 z-50 flex flex-col items-end gap-3 pointer-events-none">
            {/* Chat Window */}
            {isOpen && (
                <div className="w-[calc(100vw-3rem)] sm:w-96 h-[60vh] sm:h-[600px] pointer-events-auto transform transition-all duration-300 origin-bottom-right mb-2">
                    <div className="w-full h-full bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 animate-slide-up">
                        {renderShohAIContent()}
                    </div>
                </div>
            )}
            
            {/* Toggle Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="pointer-events-auto bg-gradient-to-r from-primary-600 to-red-600 hover:from-primary-700 hover:to-red-700 text-white rounded-full transition-all duration-300 hover:scale-110 shadow-2xl z-50 flex items-center justify-center overflow-hidden"
                style={{
                  width: '3.5rem',
                  height: '3.5rem',
                  border: isOpen ? '2px solid #e03761' : 'none',
                  boxShadow: isOpen ? '0 0 0 4px #fce7ea' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                }}
                title="Chat with shohAI"
            >
                <div className="w-full h-full flex items-center justify-center">
                    <Bot className="w-7 h-7" />
                </div>
            </button>
        </div>
      );
  }

  // Active Shift "Bundle" Mode
  return (
    <div className="fixed bottom-24 sm:bottom-6 right-6 z-50 flex flex-col items-end gap-3 pointer-events-none">
        {/* Chat Window - Positioned above the heads */}
        {isOpen && (
             <div className="w-[calc(100vw-3rem)] sm:w-96 h-[55vh] sm:h-[500px] pointer-events-auto transform transition-all duration-300 origin-bottom-right mb-2">
                 <div className="w-full h-full bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 animate-slide-up">
                    {activeTab === 'ai' ? renderShohAIContent() : renderActiveShiftChat()}
                 </div>
             </div>
        )}
        
        {/* Chat Heads Stack/Row */}
        <div className={`pointer-events-auto transition-all duration-300 ${isOpen ? 'flex flex-row-reverse items-center gap-3' : 'relative w-16 h-16 flex items-center justify-center'}`}>
             
             {(!isOpen || activeTab === 'ai') && (
             <button
                onClick={() => {
                    if (isOpen && activeTab === 'ai') { setIsOpen(false); }
                    else { setIsOpen(true); setActiveTab('ai'); }
                }}
                title="shohAI"
                style={{ zIndex: !isOpen && activeTab === 'ai' ? 20 : 10 }}
                className={`transition-all duration-300 rounded-full shadow-lg overflow-hidden bg-white border-2 flex items-center justify-center hover:scale-110
                    ${isOpen 
                        ? (activeTab === 'ai' ? 'ring-4 ring-primary-100 border-primary-500 w-14 h-14 static' : 'hidden') 
                        : (activeTab === 'ai' ? 'absolute inset-0 w-14 h-14 m-auto' : 'absolute inset-0 w-14 h-14 m-auto scale-95 opacity-0')
                    }
                    ${isOpen && activeTab !== 'ai' ? 'hidden' : ''}
                `}
             >
                 <div className="w-full h-full bg-gradient-to-br from-primary-600 to-red-600 flex items-center justify-center text-white">
                    <Bot className={isOpen ? "w-7 h-7" : "w-7 h-7"} />
                 </div>
             </button>
             )}

             {(!isOpen || activeTab === 'human') && (
             <button
                onClick={() => {
                     setHasNewMessage(false);
                     if (isOpen && activeTab === 'human') { setIsOpen(false); }
                     else { setIsOpen(true); setActiveTab('human'); }
                }}
                title={`Chat with ${activeShift.correspondentName}`}
                style={{ zIndex: !isOpen && activeTab === 'human' ? 20 : 10 }}
                className={`transition-all duration-300 rounded-full shadow-lg overflow-hidden bg-white border-2 hover:scale-110
                    ${isOpen 
                        ? (activeTab === 'human' ? 'ring-4 ring-green-100 border-green-500 w-14 h-14 static' : 'hidden') 
                        : (activeTab === 'human' ? 'absolute inset-0 w-14 h-14 m-auto' : 'absolute inset-0 w-14 h-14 m-auto scale-95 opacity-0')
                    }
                    ${isOpen && activeTab !== 'human' ? 'hidden' : ''}
                `}
             >
                 <img 
                    src={activeShift.correspondentPhoto ? getFileUrl(activeShift.correspondentPhoto) : 'https://placehold.co/100x100'} 
                    className="w-full h-full object-cover"
                 />
                 
                 {/* Online/New Message Indicator - Only show on top or when open */}
                 {(isOpen || activeTab === 'human' || hasNewMessage) && (
                     hasNewMessage ? (
                        <span className="absolute -top-1 -right-1 flex h-4 w-4 z-30">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 border-2 border-white"></span>
                        </span>
                     ) : (
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full z-30"></span>
                     )
                 )}
             </button>
             )}

             {/* If open, show the INACTIVE head as a smaller bubble to switch */}
             {isOpen && (
                 <button
                    onClick={() => {
                        const newTab = activeTab === 'ai' ? 'human' : 'ai';
                        setActiveTab(newTab);
                        if (newTab === 'human') setHasNewMessage(false);
                    }}
                    className="w-10 h-10 rounded-full shadow-md overflow-hidden bg-white border-2 border-gray-100 hover:scale-110 transition-transform opacity-70 hover:opacity-100"
                    title={activeTab === 'ai' ? "Switch to Chat" : "Switch to shohAI"}
                 >
                    {activeTab === 'ai' ? (
                       <div className="relative w-full h-full"> 
                            <img 
                                src={activeShift.correspondentPhoto ? getFileUrl(activeShift.correspondentPhoto) : 'https://placehold.co/100x100'} 
                                className="w-full h-full object-cover"
                            />
                            {hasNewMessage && <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border border-white"></span>}
                       </div>
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary-600 to-red-600 flex items-center justify-center text-white">
                            <Bot className="w-5 h-5" />
                        </div>
                    )}
                 </button>
             )}
        </div>
    </div>
  );
};

export default ShohAI;
